/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { BufferAttribute, BufferGeometry, Mesh, Object3D } from 'three';
/**
 * The text-align is used for aligning the inner content of a Text2D horizontally
 */
export var Text2DAlignment;
(function (Text2DAlignment) {
    Text2DAlignment[Text2DAlignment["LEFT"] = 0] = "LEFT";
    Text2DAlignment[Text2DAlignment["CENTER"] = 1] = "CENTER";
    Text2DAlignment[Text2DAlignment["RIGHT"] = 2] = "RIGHT";
    Text2DAlignment[Text2DAlignment["BLOCK"] = 3] = "BLOCK";
})(Text2DAlignment || (Text2DAlignment = {}));
/**
 * The text-align is used for aligning the inner content of a Text2D vertically
 */
export var Text2DVerticalAlignment;
(function (Text2DVerticalAlignment) {
    Text2DVerticalAlignment[Text2DVerticalAlignment["TOP"] = 0] = "TOP";
    Text2DVerticalAlignment[Text2DVerticalAlignment["CENTER"] = 1] = "CENTER";
    Text2DVerticalAlignment[Text2DVerticalAlignment["BOTTOM"] = 2] = "BOTTOM";
})(Text2DVerticalAlignment || (Text2DVerticalAlignment = {}));
/**
 * Class for 2d text rendering
 */
export class Text2D extends Object3D {
    constructor(font, flipY = false) {
        super();
        this._text = '';
        // alignment
        this._box = { left: 0, top: 0, right: 10, bottom: 1 };
        this._lineHeight = 1.0;
        this._autoWrapping = false;
        this._alignment = Text2DAlignment.LEFT;
        this._vAlignment = Text2DVerticalAlignment.TOP;
        this._lineSpacing = 0.0;
        this._font = font;
        this.enableFlipY(flipY);
    }
    enableFlipY(flipY) {
        this._flipY = flipY;
    }
    /**
     * sets the line heigth, note: this defines the size of the font
     * @param lineheight
     */
    setLineHeight(lineheight) {
        this._lineHeight = lineheight;
        this._create();
    }
    /**
     * sets the line spacing, the size between the lines (is added to the lineheight, but without changing the fontsize)
     * @param lineSpacing
     */
    setLineSpacing(lineSpacing) {
        this._lineSpacing = lineSpacing;
        this._create();
    }
    /**
     * sets the Text2DBox, that is the border where the text should be aligned in
     * @param box
     */
    setTextBox(box) {
        this._box = box;
        this._create();
    }
    /**
     * sets the (horizontal) alignment (e.g. LEFT, RIGHT, CENTER, BLOCK)
     * @param alignment sets the alignment
     */
    setAlignment(alignment) {
        this._alignment = alignment;
        this._create();
    }
    /**
     * sets the vertical alignment (e.g. TOP, CENTER, BOTTOM)
     * @param alignment
     */
    setVerticalAlignment(alignment) {
        this._vAlignment = alignment;
        this._create();
    }
    /**
     * sets a new font
     * @param font
     */
    setFont(font) {
        this._font = font;
        this._create();
    }
    /**
     * sets a new style
     * @param font
     */
    /*  setStyle(style: Text2DStyle) {
        // this._style = style;
        this._create();
      }
    
      /**
       * enables auto wrapping, the line is wrapped so that the words fits into the textbox
       * words do not wrap
       * @param autoWrapping
       */
    enableAutoWrapping(autoWrapping) {
        this._autoWrapping = autoWrapping;
        this._create();
    }
    /**
     * sets the text
     * @param text
     * @param clearNewLines if true, then the newlines are removed, important if the vertical alignment is set to block
     */
    setText(text, clearNewLines = false) {
        this._text = clearNewLines ? text.split('\n').join(' ') : text;
        this._create();
    }
    updateMatrix() {
        super.updateMatrix();
        this._mesh.matrix.copy(this.matrix);
    }
    updateMatrixWorld(force) {
        super.updateMatrixWorld(force);
        this._mesh.matrixWorld.copy(this.matrixWorld);
    }
    _lookAhead(iStart) {
        let spacesPerLine = 0;
        let spaceRepetitions = 0;
        let spaceFlip = true;
        let lfwX = 0;
        let advX = 0;
        let storedLfwX = 0;
        let storedE = iStart;
        const scale = this._lineHeight / this._font.getLineHeight();
        const left = this._box.left / scale;
        const right = this._box.right / scale;
        let e = iStart;
        let nexts = this._text.length;
        for (let i = iStart; i < this._text.length; i++) {
            const ch = this._text.charAt(i);
            if (ch === '\n') {
                e = i;
                nexts = i + 1;
                break;
            }
            const cp = this._text.charCodeAt(i);
            const desc = this._font.getLetterDesc(cp);
            advX += desc.xadvance;
            const isSpace = /\s/.test(ch);
            if (!isSpace) {
                e = i + 1;
                lfwX = advX;
                spaceFlip = true;
            }
            else {
                if (spaceFlip) {
                    spaceRepetitions = 0;
                    spaceFlip = false;
                }
                spacesPerLine++;
                spaceRepetitions++;
                storedE = e;
                storedLfwX = lfwX;
            }
            if (this._autoWrapping || this._alignment === Text2DAlignment.BLOCK) {
                if (advX >= (right - left) && spacesPerLine > 0) {
                    e = storedE;
                    lfwX = storedLfwX;
                    nexts = this._eatSpaces(e);
                    spacesPerLine -= spaceRepetitions;
                    break;
                }
            }
            nexts = i + 1;
        }
        return { s: iStart, e, nexts, lfwX, spGap: (spacesPerLine > 0) ? ((right - left) - lfwX) / spacesPerLine : 0.0 };
    }
    _eatSpaces(e) {
        let i = e;
        while (i < this._text.length) {
            const ch = this._text.charAt(i);
            if (!/\s/.test(ch) || ch === '\n') {
                break;
            }
            i++;
        }
        return i;
    }
    _calcLineStart(lineWidth) {
        const scale = this._lineHeight / this._font.getLineHeight();
        const left = this._box.left / scale;
        const right = this._box.right / scale;
        switch (this._alignment) {
            case Text2DAlignment.RIGHT:
                return right - lineWidth;
            case Text2DAlignment.CENTER:
                return (right + left - lineWidth) / 2.0;
            case Text2DAlignment.LEFT:
            case Text2DAlignment.BLOCK:
            default:
                return left;
        }
    }
    _calcVerticalOffset(height) {
        // const scale = this._lineHeight / this._font.getLineHeight();
        const b = this._flipY ? this._box.top : this._box.bottom;
        const t = this._flipY ? this._box.bottom : this._box.top;
        switch (this._vAlignment) {
            case Text2DVerticalAlignment.BOTTOM:
                return height + b;
            case Text2DVerticalAlignment.CENTER:
                return (t + b + height) / 2.0;
            default:
                return t;
        }
    }
    _create() {
        this.children.pop();
        if (this._text == null || this._text === '') {
            return;
        }
        const tsize = this._font.getTextureSize();
        // const base = this._font.getBase();
        const fLineHeight = this._font.getLineHeight();
        const scale = this._lineHeight / fLineHeight;
        const scaleY = scale * (this._flipY ? -1 : 1);
        const positions = [];
        const uv = [];
        let y = 0;
        let j = 0;
        while (j < this._text.length) {
            const { s, e, nexts, lfwX, spGap } = this._lookAhead(j);
            j = nexts;
            let x = this._calcLineStart(lfwX);
            for (let i = s; i < e; i++) {
                const cp = this._text.charCodeAt(i);
                if (this._alignment === Text2DAlignment.BLOCK && cp === 32) {
                    x += spGap;
                }
                const desc = this._font.getLetterDesc(cp);
                const x0 = (x + desc.xoffset) * scale;
                const y1 = (y - desc.yoffset) * scaleY;
                const x1 = (x0 + desc.width * scale);
                const y0 = (y1 - desc.height * scaleY);
                const u0 = (desc.x) / tsize;
                const v0 = (tsize - desc.y - desc.height) / tsize;
                const u1 = (desc.x + desc.width) / tsize;
                const v1 = (tsize - desc.y) / tsize;
                positions.push(x0, y0, 0, x1, y1, 0, x0, y1, 0, x0, y0, 0, x1, y0, 0, x1, y1, 0);
                uv.push(u0, v0, u1, v1, u0, v1, u0, v0, u1, v0, u1, v1);
                x += desc.xadvance; // TODO : calculate kerning etc.
            }
            y -= (fLineHeight + this._lineSpacing / scale);
        }
        const geometry = new BufferGeometry();
        const pvs = new Float32Array(positions);
        const tvs = new Float32Array(uv);
        geometry.addAttribute('position', new BufferAttribute(pvs, 3));
        geometry.addAttribute('uv', new BufferAttribute(tvs, 2));
        geometry.addGroup(0, positions.length / 3, 0);
        geometry.translate(0, this._calcVerticalOffset(-y * scaleY), 0);
        this._mesh = new Mesh(geometry, this._font.getMaterials());
        this._mesh.matrixAutoUpdate = false;
        this.children.push(this._mesh);
    }
}
//# sourceMappingURL=Text2D.js.map